import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UserMention } from './UserMention';
import { cn } from '@/lib/utils';

interface Member {
  id: string;
  username: string;
  rawUsername: string;
  displayName: string;
  avatarUrl: string | null;
  bot: boolean;
}

interface Props {
  guildId: string;
  value: string | null;
  onChange: (userId: string | null) => void;
  /** Also allow entering a raw Discord ID for users not in the guild. Default true. */
  allowRawId?: boolean;
  placeholder?: string;
  className?: string;
  /** Include bots in the results. Default false. */
  includeBots?: boolean;
}

/**
 * User picker with server-member search + optional raw-ID fallback.
 * Data source: `/api/guilds/:id/members` (60s cache). Uses the same Members
 * endpoint the Members-Page consumes, so no extra Discord API calls.
 *
 * When `value` is set, shows the user chip with a clear button. Click the chip
 * to change selection. Type text → dropdown of matches (max 8). If it looks
 * like a snowflake and doesn't match, offers a "use ID X" action.
 */
export function UserPicker({
  guildId,
  value,
  onChange,
  allowRawId = true,
  placeholder = 'User suchen oder ID eingeben',
  className,
  includeBots = false,
}: Props) {
  const membersQuery = useQuery({
    queryKey: ['members', guildId],
    queryFn: () => api<{ members: Member[] }>(`/api/guilds/${guildId}/members`),
    enabled: !!guildId,
    staleTime: 60_000,
  });

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputWrapRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const computePos = () => {
    const r = inputWrapRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
  };
  useLayoutEffect(() => {
    if (open) computePos();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const handler = () => computePos();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open]);

  // Click-outside closes dropdown.
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (
        !inputWrapRef.current?.contains(e.target as Node) &&
        !dropdownRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const members = membersQuery.data?.members ?? [];
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members
      .filter((m) => includeBots || !m.bot)
      .filter((m) => {
        if (!q) return true;
        return (
          m.displayName.toLowerCase().includes(q) ||
          m.rawUsername.toLowerCase().includes(q) ||
          m.id.includes(q)
        );
      })
      .slice(0, 8);
  }, [members, query, includeBots]);

  // ID-fallback: if the query is a snowflake and doesn't match a member.
  const rawIdCandidate =
    allowRawId && /^\d{17,20}$/.test(query.trim()) && !members.find((m) => m.id === query.trim())
      ? query.trim()
      : null;

  // If value is set, show the chip.
  if (value) {
    return (
      <div className={cn('flex items-center gap-2 rounded-md border bg-background p-2', className)}>
        <div className="min-w-0 flex-1">
          <UserMention id={value} />
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onChange(null)}
          title="Auswahl entfernen"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  const dropdownVisible =
    open &&
    (matches.length > 0 ||
      rawIdCandidate ||
      membersQuery.isLoading ||
      membersQuery.error);

  return (
    <div ref={inputWrapRef} className={cn('relative', className)}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="pl-8"
        />
      </div>

      {dropdownVisible &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              width: pos.width,
            }}
            className="glass-float z-[100] max-h-72 overflow-y-auto rounded-md border bg-card shadow-2xl"
          >
          {membersQuery.isLoading && (
            <p className="p-2 text-xs text-muted-foreground">Lade Members…</p>
          )}
          {membersQuery.error && (
            <p className="p-2 text-xs text-destructive">
              {(membersQuery.error as Error).message}
            </p>
          )}
          {matches.length === 0 && !membersQuery.isLoading && !rawIdCandidate && (
            <p className="p-2 text-xs text-muted-foreground">Keine Treffer.</p>
          )}
          {matches.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                onChange(m.id);
                setQuery('');
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-accent"
            >
              {m.avatarUrl ? (
                <img src={m.avatarUrl} alt="" className="h-6 w-6 rounded-full" loading="lazy" />
              ) : (
                <div className="h-6 w-6 rounded-full bg-muted" />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate">{m.displayName}</div>
                <div className="truncate text-[10px] text-muted-foreground">
                  @{m.rawUsername} · {m.id}
                </div>
              </div>
              {m.bot && (
                <span className="rounded-md bg-primary/20 px-1 text-[9px] text-primary">BOT</span>
              )}
            </button>
          ))}
          {rawIdCandidate && (
            <button
              type="button"
              onClick={() => {
                onChange(rawIdCandidate);
                setQuery('');
                setOpen(false);
              }}
              className="flex w-full items-center gap-2 border-t px-2 py-1.5 text-left text-sm hover:bg-accent"
            >
              <div className="h-6 w-6 rounded-full bg-muted" />
              <div className="min-w-0 flex-1">
                <div className="text-xs">
                  Discord-ID direkt verwenden:{' '}
                  <code className="text-[11px]">{rawIdCandidate}</code>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  User ist ggf. nicht (mehr) auf diesem Server.
                </div>
              </div>
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
